import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  TextField,
  CircularProgress,
  Switch,
  FormControlLabel,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LanguageIcon from '@mui/icons-material/Language';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShareIcon from '@mui/icons-material/Share';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import {
  getGoogleSearchSettings,
  getHeaderConfig,
  saveGoogleSearchSettings,
  type GoogleSearchFaq,
  type GoogleSearchSettingsData,
  type GoogleSearchSitelink,
} from '../../../services/ecommerceApi';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

interface GoogleSearchSettingsProps {
  companyId: string | null;
}

const MAX_SITELINKS = 6;
const MAX_FAQS = 6;

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const emptySettings = (): GoogleSearchSettingsData => ({
  enabled: true,
  title: '',
  description: '',
  websiteUrl: '',
  knowledgeName: '',
  knowledgeDescription: '',
  address: '',
  phone: '',
  email: '',
  logoUrl: '',
  sitelinks: [],
  faqs: [],
});

const formatDisplayUrl = (raw: string) => {
  const value = raw.trim();
  if (!value) return 'www.ornek.com';
  return value.replace(/^https?:\/\//i, '').replace(/\/$/, '');
};

const GoogleSearchSettings: React.FC<GoogleSearchSettingsProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<GoogleSearchSettingsData>(emptySettings());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!companyId) return;
      if (!loadedRef.current) setLoading(true);
      try {
        const [searchResponse, headerResponse] = await Promise.all([
          getGoogleSearchSettings(companyId),
          getHeaderConfig(companyId).catch(() => null),
        ]);
        if (cancelled) return;
        const next = { ...emptySettings(), ...(searchResponse?.data || {}) };
        next.sitelinks = Array.isArray(next.sitelinks) ? next.sitelinks : [];
        next.faqs = Array.isArray(next.faqs) ? next.faqs : [];
        if (!next.logoUrl && headerResponse?.data?.logoUrl) {
          next.logoUrl = headerResponse.data.logoUrl;
        }
        settingsRef.current = next;
        setSettings(next);
        loadedRef.current = true;
      } catch (error) {
        console.error('❌ [GOOGLE-SEARCH] Ayarlar yüklenemedi:', error);
        if (!cancelled) {
          setFeedback({ type: 'error', message: t('ecommerceGeneral.googleSearch.loadError') });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // t kasıtlı olarak bağımlılıkta değil: i18n referansı değişince formu
    // API'den yeniden yüklemek yazarken silinmeye yol açıyordu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const persist = async (next: GoogleSearchSettingsData) => {
    if (!companyId) return;
    setSaving(true);
    setFeedback(null);
    try {
      await saveGoogleSearchSettings(next, companyId);
      notifyEcommerceSaved(t('ecommerceGeneral.googleSearch.saveSuccess'));
    } catch (error) {
      console.error('❌ [GOOGLE-SEARCH] Ayar kaydedilemedi:', error);
      setFeedback({ type: 'error', message: t('ecommerceGeneral.googleSearch.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const schedulePersist = (next: GoogleSearchSettingsData) => {
    if (!loadedRef.current || !companyId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(next), 600);
  };

  const applyLocal = (next: GoogleSearchSettingsData, persistNow: boolean) => {
    settingsRef.current = next;
    setSettings(next);
    if (persistNow) schedulePersist(next);
  };

  const update = (patch: Partial<GoogleSearchSettingsData>) => {
    applyLocal({ ...settingsRef.current, ...patch }, true);
  };

  const updateSitelink = (index: number, patch: Partial<GoogleSearchSitelink>) => {
    const sitelinks = settingsRef.current.sitelinks.map((link, i) =>
      i === index ? { ...link, ...patch } : link
    );
    update({ sitelinks });
  };

  const addSitelink = () => {
    if (settingsRef.current.sitelinks.length >= MAX_SITELINKS) return;
    applyLocal(
      {
        ...settingsRef.current,
        sitelinks: [...settingsRef.current.sitelinks, { id: newId(), title: '', url: '' }],
      },
      false
    );
  };

  const removeSitelink = (index: number) => {
    update({ sitelinks: settingsRef.current.sitelinks.filter((_, i) => i !== index) });
  };

  const updateFaq = (index: number, patch: Partial<GoogleSearchFaq>) => {
    const faqs = settingsRef.current.faqs.map((faq, i) => (i === index ? { ...faq, ...patch } : faq));
    update({ faqs });
  };

  const addFaq = () => {
    if (settingsRef.current.faqs.length >= MAX_FAQS) return;
    applyLocal(
      {
        ...settingsRef.current,
        faqs: [...settingsRef.current.faqs, { id: newId(), question: '', answer: '' }],
      },
      false
    );
  };

  const removeFaq = (index: number) => {
    update({ faqs: settingsRef.current.faqs.filter((_, i) => i !== index) });
  };

  const previewTitle = settings.title.trim() || t('ecommerceGeneral.googleSearch.previewEmptyTitle');
  const previewName = settings.knowledgeName.trim() || previewTitle;
  const previewDescription =
    settings.description.trim() || t('ecommerceGeneral.googleSearch.previewEmptyDescription');
  const previewHost = formatDisplayUrl(settings.websiteUrl);
  const visibleSitelinks = settings.sitelinks.filter((link) => link.title.trim());
  const visibleFaqs = settings.faqs.filter((faq) => faq.question.trim());
  const logoSrc = settings.logoUrl.trim();


  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <TravelExploreIcon sx={{ mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6">{t('ecommerceGeneral.googleSearch.title')}</Typography>
        {saving && <CircularProgress size={18} sx={{ ml: 1.5 }} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceGeneral.googleSearch.help')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled}
                disabled={!companyId}
                onChange={(e) => update({ enabled: e.target.checked })}
              />
            }
            label={
              settings.enabled
                ? t('ecommerceGeneral.googleSearch.enabled')
                : t('ecommerceGeneral.googleSearch.disabled')
            }
            sx={{ mb: 2 }}
          />

          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: '#202124',
              border: '1px solid #3c4043',
            }}
          >
            <Typography variant="caption" sx={{ color: '#9aa0a6', display: 'block', mb: 1.5 }}>
              {t('ecommerceGeneral.googleSearch.previewLabel')}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 280px' },
                gap: 3,
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      backgroundColor: '#3c4043',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {logoSrc ? (
                      <Box
                        component="img"
                        src={logoSrc}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : null}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#e8eaed', fontSize: 13, lineHeight: 1.2 }}>
                      {previewName}
                    </Typography>
                    <Typography sx={{ color: '#9aa0a6', fontSize: 12 }}>
                      https://{previewHost}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  sx={{
                    color: '#8ab4f8',
                    fontSize: 22,
                    fontWeight: 400,
                    lineHeight: 1.25,
                    mb: 0.5,
                  }}
                >
                  {previewTitle}
                </Typography>
                <Typography sx={{ color: '#bdc1c6', fontSize: 14, lineHeight: 1.45, mb: 1.5 }}>
                  {previewDescription}
                </Typography>

                {visibleSitelinks.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      columnGap: 3,
                      rowGap: 1.25,
                    }}
                  >
                    {visibleSitelinks.map((link) => (
                      <Box key={link.id || link.title}>
                        <Typography sx={{ color: '#8ab4f8', fontSize: 14 }}>{link.title}</Typography>
                        <Typography sx={{ color: '#9aa0a6', fontSize: 12 }}>
                          {link.url || '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {visibleFaqs.length > 0 && (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ color: '#e8eaed', fontSize: 16, mb: 1 }}>
                      {t('ecommerceGeneral.googleSearch.peopleAlsoAsk')}
                    </Typography>
                    {visibleFaqs.map((faq) => (
                      <Box
                        key={faq.id || faq.question}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: '1px solid #3c4043',
                          py: 1.1,
                        }}
                      >
                        <Typography sx={{ color: '#e8eaed', fontSize: 14 }}>{faq.question}</Typography>
                        <KeyboardArrowDownIcon sx={{ color: '#9aa0a6', fontSize: 20 }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  border: '1px solid #3c4043',
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: '#171717',
                }}
              >
                {logoSrc && (
                  <Box
                    component="img"
                    src={logoSrc}
                    alt=""
                    sx={{
                      width: '100%',
                      height: 88,
                      objectFit: 'contain',
                      backgroundColor: '#fff',
                      borderRadius: 1,
                      mb: 1.25,
                    }}
                  />
                )}
                <Typography sx={{ color: '#e8eaed', fontSize: 20, fontWeight: 500, mb: 1 }}>
                  {previewName}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {[
                    { icon: <LanguageIcon sx={{ fontSize: 16 }} />, label: t('ecommerceGeneral.googleSearch.panelWebsite') },
                    { icon: <PlaceIcon sx={{ fontSize: 16 }} />, label: t('ecommerceGeneral.googleSearch.panelDirections') },
                    { icon: <BookmarkBorderIcon sx={{ fontSize: 16 }} />, label: t('ecommerceGeneral.googleSearch.panelSave') },
                    { icon: <ShareIcon sx={{ fontSize: 16 }} />, label: t('ecommerceGeneral.googleSearch.panelShare') },
                    { icon: <PhoneIcon sx={{ fontSize: 16 }} />, label: t('ecommerceGeneral.googleSearch.panelCall') },
                  ].map((action) => (
                    <Box
                      key={action.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.4,
                        borderRadius: 5,
                        border: '1px solid #5f6368',
                        color: '#8ab4f8',
                        fontSize: 11,
                      }}
                    >
                      {action.icon}
                      {action.label}
                    </Box>
                  ))}
                </Box>
                {settings.knowledgeDescription.trim() && (
                  <Typography sx={{ color: '#bdc1c6', fontSize: 12, mb: 1.25 }}>
                    {settings.knowledgeDescription}
                  </Typography>
                )}
                {settings.address.trim() && (
                  <Typography sx={{ color: '#bdc1c6', fontSize: 12, mb: 0.5 }}>
                    {settings.address}
                  </Typography>
                )}
                {settings.phone.trim() && (
                  <Typography sx={{ color: '#8ab4f8', fontSize: 12 }}>{settings.phone}</Typography>
                )}
              </Box>
            </Box>
          </Box>

          <TextField
            fullWidth
            label={t('ecommerceGeneral.googleSearch.resultTitle')}
            helperText={t('ecommerceGeneral.googleSearch.resultTitleHelp')}
            value={settings.title}
            disabled={!companyId}
            inputProps={{ maxLength: 70 }}
            onChange={(e) => update({ title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('ecommerceGeneral.googleSearch.resultDescription')}
            helperText={t('ecommerceGeneral.googleSearch.resultDescriptionHelp', {
              count: settings.description.length,
            })}
            value={settings.description}
            disabled={!companyId}
            inputProps={{ maxLength: 160 }}
            onChange={(e) => update({ description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('ecommerceGeneral.googleSearch.websiteUrl')}
            helperText={t('ecommerceGeneral.googleSearch.websiteUrlHelp')}
            value={settings.websiteUrl}
            disabled={!companyId}
            placeholder="https://www.hacioglu.com"
            onChange={(e) => update({ websiteUrl: e.target.value })}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('ecommerceGeneral.googleSearch.sitelinksTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceGeneral.googleSearch.sitelinksHelp')}
          </Typography>

          {settings.sitelinks.map((link, index) => (
            <Box key={link.id || index} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
              <TextField
                label={t('ecommerceGeneral.googleSearch.sitelinkTitle')}
                value={link.title}
                disabled={!companyId}
                onChange={(e) => updateSitelink(index, { title: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label={t('ecommerceGeneral.googleSearch.sitelinkUrl')}
                value={link.url}
                disabled={!companyId}
                placeholder="/about"
                onChange={(e) => updateSitelink(index, { url: e.target.value })}
                sx={{ flex: 1 }}
              />
              <IconButton
                color="error"
                onClick={() => removeSitelink(index)}
                disabled={!companyId}
                sx={{ mt: 0.5 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addSitelink}
            disabled={!companyId || settings.sitelinks.length >= MAX_SITELINKS}
            sx={{ mb: 3 }}
          >
            {t('ecommerceGeneral.googleSearch.addSitelink')}
          </Button>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('ecommerceGeneral.googleSearch.panelTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceGeneral.googleSearch.panelHelp')}
          </Typography>
          <TextField
            fullWidth
            label={t('ecommerceGeneral.googleSearch.knowledgeName')}
            value={settings.knowledgeName}
            disabled={!companyId}
            onChange={(e) => update({ knowledgeName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('ecommerceGeneral.googleSearch.knowledgeDescription')}
            value={settings.knowledgeDescription}
            disabled={!companyId}
            onChange={(e) => update({ knowledgeDescription: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('ecommerceGeneral.googleSearch.address')}
            value={settings.address}
            disabled={!companyId}
            onChange={(e) => update({ address: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label={t('ecommerceGeneral.googleSearch.phone')}
              value={settings.phone}
              disabled={!companyId}
              onChange={(e) => update({ phone: e.target.value })}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <TextField
              label={t('ecommerceGeneral.googleSearch.email')}
              value={settings.email}
              disabled={!companyId}
              onChange={(e) => update({ email: e.target.value })}
              sx={{ flex: 1, minWidth: 220 }}
            />
          </Box>
          <TextField
            fullWidth
            label={t('ecommerceGeneral.googleSearch.logoUrl')}
            helperText={t('ecommerceGeneral.googleSearch.logoUrlHelp')}
            value={settings.logoUrl}
            disabled={!companyId}
            onChange={(e) => update({ logoUrl: e.target.value })}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('ecommerceGeneral.googleSearch.faqsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceGeneral.googleSearch.faqsHelp')}
          </Typography>
          {settings.faqs.map((faq, index) => (
            <Box key={faq.id || index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label={t('ecommerceGeneral.googleSearch.faqQuestion')}
                  value={faq.question}
                  disabled={!companyId}
                  onChange={(e) => updateFaq(index, { question: e.target.value })}
                  sx={{ mb: 1 }}
                />
                <IconButton color="error" onClick={() => removeFaq(index)} disabled={!companyId} sx={{ mt: 0.5 }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={t('ecommerceGeneral.googleSearch.faqAnswer')}
                value={faq.answer}
                disabled={!companyId}
                onChange={(e) => updateFaq(index, { answer: e.target.value })}
              />
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addFaq}
            disabled={!companyId || settings.faqs.length >= MAX_FAQS}
          >
            {t('ecommerceGeneral.googleSearch.addFaq')}
          </Button>

          {feedback && (
            <Alert severity={feedback.type} sx={{ mt: 2 }}>
              {feedback.message}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default GoogleSearchSettings;
